import { showSnackbarWithMessage } from '../Snackbar/snackbar.saga';
import { Story } from '../Entities/Stories/stories.reducer';
import {
  getCurrentClusterEditStoryIds,
  getCurrentEditClusterState,
  getClusterEdit,
  getIsFeaturedClusterAppIds,
} from './cluster-edit.reducer';
import { updateStories } from '../Entities/Stories/stories.actions';
import { callApi } from '../api';
import {
  fetchClusterCallOptions,
  postClusterCallOptions,
  putClusterCallOptions,
  deleteClusterCallOptions,
} from '../../services/clusterlist.services';
import { call, put, take, select, takeLatest } from 'redux-saga/effects';
import {
  fetchClusterById,
  hideClusterEditLoading,
  removeClusterEditStory,
  setClusterEditStories,
  showClusterEditLoading,
  submitClusterEdit,
  updateClusterEdit,
  updateClusterEditStories,
  deleteCluster,
  reOrderClusterEditStories,
} from './cluster-edit.actions';
import { normalize, schema } from 'normalizr';
import {
  indexBy,
  reject,
  omit,
  map,
  pick,
  merge,
  pluck,
  prop,
  last,
} from 'ramda';
import { gotoSite } from '../Navigation/navigation.actions';
import { Action } from 'typescript-fsa';
import {
  fetchFeaturedClusters,
  saveFeaturedClusters,
} from '../Entities/FeaturedCluster/featured-cluster.saga';
import {
  getDirtyFeaturedClusters,
  getFeaturedClustersByClusterId,
  FeaturedCluster,
} from '../Entities/FeaturedCluster/featured-cluster.reducer';
import { countries } from '../../utils/country';
import { featuredClusterEditRemoveCluster } from '../FeaturedClusterEdit/featured-cluster-edit.actions';
import { Cluster } from '../Entities/Clusters/clusters.reducer';
import {
  removeFeaturedCluster,
  addFeaturedCluster,
  updateFeaturedClusters,
} from '../Entities/FeaturedCluster/featured-cluster.actions';

function normalizeCluster(entitiy: any) {
  return {
    ...entitiy,
    stories: entitiy.stories.data,
  };
}

function normalizeStory(entity: any) {
  return {
    ...entity,
    appConfigs: entity.appConfigs.data,
  };
}

export const clusterStorySchema = new schema.Entity(
  'story',
  {},
  {
    idAttribute: 'id',
    processStrategy: normalizeStory,
  }
);

export const clusterSchema = new schema.Entity(
  'cluster',
  {
    stories: [clusterStorySchema],
  },
  {
    processStrategy: normalizeCluster,
    idAttribute: 'id',
  }
);

function* doUpdate(data: any) {
  yield call(fetchFeaturedClusters);

  const featuredClusters = yield select(
    getFeaturedClustersByClusterId,
    data.id
  );

  const enhancedClusterData = {
    ...data,
    isFeatured: featuredClusters.reduce((acc, cur) => {
      const country = countries.find(c => c.id === cur.appId);

      if (!country) {
        return acc;
      }

      return { ...acc, [country.shortcode]: true };
    }, {}),
    gender:
      featuredClusters && featuredClusters[0]
        ? featuredClusters[0].gender
        : 'female',
    activePeriod: featuredClusters.reduce((acc, cur) => {
      const { activeFrom, activeTo } = cur;
      const country = countries.find(c => c.id === cur.appId);

      if (!country) {
        return acc;
      }

      return {
        ...acc,
        [country.shortcode]: {
          activeFrom,
          activeTo,
        },
      };
    }, {}),
  };

  const normalized = normalize(enhancedClusterData, clusterSchema);
  const {
    entities: { cluster, story },
  } = normalized;

  yield put(updateStories(story));
  yield put(
    updateClusterEdit({ cluster: cluster && cluster[Object.keys(cluster)[0]] })
  );
  yield put(hideClusterEditLoading());
}

export function* watchForFetchClusterById() {
  while (true) {
    const {
      payload: { clusterId },
    } = yield take(fetchClusterById.type);

    yield put(showClusterEditLoading());

    const options = fetchClusterCallOptions(clusterId, []);
    const { ok, data } = yield call(callApi, options);

    if (ok) {
      yield doUpdate(sortStories(data));
    }
  }
}

export function* watchForSetClusterEditStories() {
  while (true) {
    const {
      payload: { stories },
    } = yield take(setClusterEditStories.type);
    const state = yield select();
    const clusterEdit = getClusterEdit(state);

    yield put(
      updateClusterEditStories({
        stories: stories && stories.map((story: Story) => story.id),
      })
    );

    if (clusterEdit && clusterEdit.id) {
      yield saveCluster();
    }
  }
}

export function* watchForRemoveClusterEditStory() {
  while (true) {
    const {
      payload: { story },
    } = yield take(removeClusterEditStory.type);
    const state = yield select();
    const stories = getCurrentClusterEditStoryIds(state);
    yield put(
      updateClusterEditStories({
        stories:
          stories &&
          reject(
            (selectedStoryId: number) => selectedStoryId === story.id,
            stories as number[]
          ),
      })
    );
    yield saveCluster();
  }
}

function denormalizeCluster(entity: any) {
  return {
    ...omit(
      ['isFeatured', 'activeFrom', 'activeTo', 'gender', 'loading'],
      entity
    ),
    stories: {
      data: map((story: any) => {
        return pick(['id'], story);
      }, entity.stories),
    },
  };
}

function* removeFeaturedClustersForAppIds(appIds) {
  const clusterEdit = yield select(getCurrentEditClusterState);
  const featuredClusters = yield select(
    getFeaturedClustersByClusterId,
    clusterEdit.id
  );

  for (const appId of appIds) {
    const featuredCluster = featuredClusters.find(c => c.appId === appId);

    if (!featuredCluster) {
      continue;
    }

    yield put(removeFeaturedCluster(featuredCluster));
    yield put(featuredClusterEditRemoveCluster(featuredCluster));
  }
}

function* addFeaturedClustersForAppIds(appIds) {
  const clusterEdit = yield select(getCurrentEditClusterState);
  const featuredClusters = yield select(
    getFeaturedClustersByClusterId,
    clusterEdit.id
  );

  for (const appId of appIds) {
    const country = countries.find(c => c.id === appId);

    if (!country) {
      continue;
    }

    const activePeriod = clusterEdit.activePeriod[country.shortcode];
    const lastFeaturedCluster: FeaturedCluster | undefined = last(
      featuredClusters
    );
    const position = lastFeaturedCluster ? lastFeaturedCluster.position + 1 : 1;
    const { gender, id: clusterId } = clusterEdit;
    const id = -1 * position * country.id;
    const newFeaturedCluster: FeaturedCluster = {
      gender,
      appId,
      id,
      clusterId,
      position,
      activeFrom: activePeriod ? activePeriod.activeFrom : null,
      activeTo: activePeriod ? activePeriod.activeTo : null,
      cluster: omit(
        ['isFeatured', 'activePeriod', 'gender'],
        clusterEdit
      ) as Cluster,
    };

    yield put(addFeaturedCluster(newFeaturedCluster));
  }
}

function* updateFeaturedClustersForAppIds(appIds) {
  const clusterEdit = yield select(getCurrentEditClusterState);
  const featuredClusters = yield select(
    getFeaturedClustersByClusterId,
    clusterEdit.id
  );

  for (const appId of appIds) {
    const featuredCluster = featuredClusters.find(c => c.appId === appId);
    const country = countries.find(c => c.id === appId);

    if (!country) {
      continue;
    }

    const activePeriod = clusterEdit.activePeriod[country.shortcode];

    if (
      activePeriod &&
      activePeriod.activeFrom === featuredCluster.activeFrom &&
      activePeriod.activeTo === featuredCluster.activeTo
    ) {
      continue;
    }

    const { gender, id: clusterId } = clusterEdit;
    const { id, position } = featuredCluster;

    yield put(
      updateFeaturedClusters({
        [id]: {
          gender,
          appId,
          id,
          clusterId,
          position,
          activeFrom: activePeriod ? activePeriod.activeFrom : null,
          activeTo: activePeriod ? activePeriod.activeTo : null,
          cluster: omit(
            ['isFeatured', 'activePeriod', 'gender'],
            clusterEdit
          ) as Cluster,
          dirty: true,
        },
      })
    );
  }
}

function* handleFeaturedClusterCUD() {
  const clusterEdit = yield select(getCurrentEditClusterState);

  yield call(fetchFeaturedClusters);

  const existingFeaturedClustersForClusterId = yield select(
    getFeaturedClustersByClusterId,
    clusterEdit.id
  );
  const newFeaturedClusterAppIds: number[] = yield select(
    getIsFeaturedClusterAppIds
  );
  const existingFeaturedClusterAppIds = existingFeaturedClustersForClusterId.map(
    f => f.appId
  );

  const add: number[] = [];
  const update: number[] = [];

  for (let index = 0; index < newFeaturedClusterAppIds.length; index++) {
    if (
      existingFeaturedClusterAppIds.includes(newFeaturedClusterAppIds[index])
    ) {
      update.push(newFeaturedClusterAppIds[index]);
    } else {
      add.push(newFeaturedClusterAppIds[index]);
    }
  }

  const remove = existingFeaturedClusterAppIds.filter(
    v => !newFeaturedClusterAppIds.includes(v)
  );

  // add, update or remove featuredClusters
  yield addFeaturedClustersForAppIds(add);
  yield updateFeaturedClustersForAppIds(update);
  yield removeFeaturedClustersForAppIds(remove);

  // flush featuredCluster changes
  yield saveFeaturedClusters(yield select(getDirtyFeaturedClusters));
}

function* saveCluster(additionalData?: object) {
  const clusterEdit = yield select(getCurrentEditClusterState);
  const { type, images } = clusterEdit;

  if (type === 'occasion') {
    if (!(images && images.data && images.data.length)) {
      yield showSnackbarWithMessage('error', 'Type occasion must have an image');
      return;
    }
  }

  yield put(showClusterEditLoading());

  //handleFeaturedClusters
  yield handleFeaturedClusterCUD();

  const denormalized = denormalizeCluster(clusterEdit);
  const callOptions = clusterEdit.id
    ? putClusterCallOptions(clusterEdit.id, denormalized, additionalData || {})
    : postClusterCallOptions(denormalized);
  const { ok, data } = yield callApi(callOptions);

  yield put(hideClusterEditLoading());

  if (ok) {
    yield doUpdate(sortStories(data));
    yield showSnackbarWithMessage('success', 'Cluster saved');
  }
}

export function* watchForSubmitClusterEdit() {
  while (true) {
    yield take(submitClusterEdit.type);

    yield saveCluster();
  }
}

export function* watchForDeleteCluster() {
  while (true) {
    const { payload } = yield take(deleteCluster.type);
    const callOptions = deleteClusterCallOptions(payload);
    const { ok } = yield callApi(callOptions);
    if (ok) {
      yield put(gotoSite('/clusters'));

      yield showSnackbarWithMessage('success', 'Cluster deleted successfully');
    }
  }
}

const sortStoriesByTheirPositionInCluster = (a: Story, b: Story) => {
  if (!a.clusterStory || !b.clusterStory) {
    return 1;
  }
  return a.clusterStory.position > b.clusterStory.position ? 1 : 0;
};

export function* watchForReorderClusterEditStories() {
  const worker = function*(action: Action<Story[]>) {
    const orderedStories = action.payload.map((item: Story, key: number) =>
      merge(item, { position: key + 1 })
    );

    yield put(
      updateClusterEditStories({ stories: pluck('id')(orderedStories) })
    );

    yield put(
      updateStories(indexBy(prop('id'), orderedStories) as {
        [key: number]: Story;
      })
    );

    yield call(saveCluster, {
      stories: { data: orderedStories },
    });
  };

  yield takeLatest(reOrderClusterEditStories.type, worker);
}

function sortStories(data: any) {
  return data.stories && data.stories.data
    ? merge(data, {
        stories: {
          data: data.stories.data.sort(sortStoriesByTheirPositionInCluster),
        },
      })
    : data;
}



// WEBPACK FOOTER //
// ./src/store/ClusterEdit/cluster-edit.saga.ts